import { turbine } from "neql"
import { useCallback, useState } from "react"
import { useDispatch } from "react-redux"
import { dirtinessChanged } from "../Actions/Client/InputRender/InputRenderActions"
import { failMessage } from "../Actions/Client/MessagesActions"
import { defaultTest } from "../Models/API/QualifierModel"
import { useNeqlContextProvider } from "./useNeqlContextProvider"

export interface TrainingTestVariables {
    id: string
    cooloff?: number
    cutoffMetricName: string
    cutoffOperator?: turbine.MetricOperator
    cutoffValue?: number
    testNumQuestions?: number
    cutoffMetricDisplayName: string
}

export const useQualifierTestCreate = (
    appID: string,
    quaifierName: string = "Test",
    reloadTests: () => void,
    maxQuestions = Infinity
): {
    sampleTest?: turbine.TrainingTest
    addSampleTest: () => void
    createOrUpdateTest: (params: TrainingTestVariables) => Promise<boolean>
    isUpdatingTest: boolean
} => {
    const clientContext = useNeqlContextProvider("turbine")
    const dispatch = useDispatch()

    const [sampleTest, setSampleTest] =
        useState<turbine.TrainingTest | undefined>()

    const [createTest, { loading: testCreationLoading }] =
        turbine.useCreateTrainingTestMutation({
            client: clientContext
        })
    const [createQuestionTest, { loading: questionTestCreationLoading }] =
        turbine.useCreateQuestionTestMutation({
            client: clientContext
        })

    const [updateTest, { loading }] = turbine.useUpdateTrainingTestMutation({
        client: clientContext
    })

    const addSampleTest = useCallback(() => {
        setSampleTest({
            ...defaultTest,
            taskInfo: {
                ...defaultTest.taskInfo,
                title: `Test: ${quaifierName}`
            },
            appUuid: appID,
            state: turbine.TrainingApplicationState.Active,
            trainingState: turbine.TrainingState.Creating,
            numQuestions: Math.min(10, maxQuestions)
        })
        dispatch(dirtinessChanged(true))
    }, [appID, dispatch, maxQuestions, quaifierName])

    const createOrUpdateTest = useCallback(
        async ({
            id,
            cooloff,
            cutoffMetricDisplayName,
            cutoffOperator,
            cutoffValue,
            cutoffMetricName,
            testNumQuestions
        }: TrainingTestVariables) => {
            try {
                if (sampleTest !== undefined && id === sampleTest.id) {
                    const createdTest = await createTest({
                        variables: {
                            createTrainingTestAppUuid: sampleTest.appUuid,
                            createTrainingTestCoolOffMinutes: cooloff ?? 60,
                            createTrainingTestCutoffMetricName:
                                cutoffMetricName,
                            createTrainingTestCutoffOperator:
                                cutoffOperator ??
                                turbine.MetricOperator.GreaterThan,
                            createTrainingTestCutoffValue: cutoffValue ?? 0.9,
                            createTrainingTestNumQuestions:
                                testNumQuestions ?? Math.min(10, maxQuestions),
                            createTrainingTestCutoffMetricDisplayName:
                                cutoffMetricDisplayName,
                            createTrainingTestTitle:
                                sampleTest.taskInfo?.title ??
                                `Test: ${quaifierName}`,
                            createTrainingTestDescription:
                                sampleTest.taskInfo?.description ??
                                "A short task summary for the Human Worker goes here..."
                        }
                    })
                    // create a test pool with the test creation
                    if (
                        createdTest.data?.createTrainingTest &&
                        !createdTest.errors
                    ) {
                        await createQuestionTest({
                            variables: {
                                createQuestionTestBaselineAppUuid: appID,
                                createQuestionTestTrainingId:
                                    createdTest.data.createTrainingTest
                                        .trainingId
                            }
                        })
                        await reloadTests()
                        setSampleTest(undefined)
                        dispatch(dirtinessChanged(false))
                        return true
                    }
                } else {
                    await updateTest({
                        variables: {
                            updateTrainingId: id,
                            updateTrainingNumQuestions: testNumQuestions,
                            updateTrainingCutoffValue: cutoffValue,
                            updateTrainingCoolOffMinutes: cooloff,
                            updateTrainingCutoffOperator: cutoffOperator
                        }
                    })
                    dispatch(dirtinessChanged(false))
                    return true
                }
            } catch (error) {
                dispatch(failMessage("Failed to update test", error))
            }
            return false
        },
        [
            appID,
            createQuestionTest,
            createTest,
            dispatch,
            maxQuestions,
            quaifierName,
            reloadTests,
            sampleTest,
            updateTest
        ]
    )

    return {
        sampleTest,
        addSampleTest,
        createOrUpdateTest,
        isUpdatingTest:
            testCreationLoading || questionTestCreationLoading || loading
    }
}
